<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useLlmCredentialStore } from 'dashboard/store/captain/llmCredentials';
import LlmCredentialsAPI from 'dashboard/api/captain/llmCredentials';
import { serverErrorMessage } from 'dashboard/store/utils/api';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import SegmentedControl from 'dashboard/components-next/segmented-control/SegmentedControl.vue';
import { LLM_PROVIDERS, providerName } from '../llmCredentials';

const emit = defineEmits(['saved']);

const { t } = useI18n();
const store = useLlmCredentialStore();

const dialogRef = ref(null);
const editingId = ref(null);
const showAdvanced = ref(false);
const blankForm = () => ({
  name: '',
  provider: 'openai',
  api_key: '',
  endpoint: '',
  model_override: '',
  token_limit: '',
  limit_period: 'month',
});
const form = ref(blankForm());

const periodOptions = computed(() => [
  { value: 'day', label: t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.PERIOD.DAY') },
  { value: 'week', label: t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.PERIOD.WEEK') },
  { value: 'month', label: t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.PERIOD.MONTH') },
]);

// A name left blank becomes the provider's, which is what most accounts would type anyway.
const isValid = computed(
  () => editingId.value || form.value.api_key.trim().length > 0
);

// The key is tried the moment it is pasted (or the field is left), against the provider and
// endpoint the form holds: a wrong key or a dead endpoint shows next to the field, not on the first
// conversation. Any change to what was tried makes the answer stale, and an answer that arrives
// after a newer try is dropped.
const check = ref({ state: 'idle', message: '' });
let checkToken = 0;
const resetCheck = () => {
  checkToken += 1;
  check.value = { state: 'idle', message: '' };
};
watch(
  () => [form.value.provider, form.value.api_key, form.value.endpoint],
  resetCheck
);
const canCheck = computed(
  () => Boolean(editingId.value) || form.value.api_key.trim().length > 0
);
const runCheck = async () => {
  if (!canCheck.value) return;
  checkToken += 1;
  const token = checkToken;
  check.value = { state: 'running', message: '' };
  const { provider, api_key: apiKey, endpoint } = form.value;
  let result;
  try {
    const { data } = await LlmCredentialsAPI.check({
      id: editingId.value,
      provider,
      api_key: apiKey.trim(),
      endpoint: endpoint.trim(),
    });
    result = data.ok
      ? {
          state: 'ok',
          message: t(
            'CAPTAIN_SETTINGS.LLM_CREDENTIALS.CHECK.OK',
            { provider: providerName(provider), count: data.models_count },
            data.models_count
          ),
        }
      : { state: 'failed', message: data.message };
  } catch (error) {
    result = {
      state: 'failed',
      message:
        serverErrorMessage(error) ||
        t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.CHECK.ERROR'),
    };
  }
  if (token === checkToken) check.value = result;
};

// Pasting is how a key arrives: try it as soon as the field holds it.
const onKeyPasted = () => nextTick(runCheck);
// Leaving a field the check has not seen yet tries what is there now.
const checkIfStale = () => {
  if (check.value.state === 'idle') runCheck();
};
const selectProvider = provider => {
  form.value.provider = provider;
  nextTick(checkIfStale);
};

const checkHint = computed(() => {
  if (check.value.state === 'running') {
    return t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.CHECK.RUNNING', {
      provider: providerName(form.value.provider),
    });
  }
  if (check.value.message) return check.value.message;
  return editingId.value
    ? t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.CHECK.HINT_KEEP')
    : t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.CHECK.HINT');
});

// `defaults` prefills a new LLM, e.g. with the provider a feature needs.
const open = (credential, defaults = {}) => {
  editingId.value = credential?.id || null;
  form.value = credential
    ? {
        name: credential.name,
        provider: credential.provider,
        // Never sent back by the API, so an untouched field leaves the stored key alone.
        api_key: '',
        endpoint: credential.endpoint || '',
        model_override: credential.model_override || '',
        token_limit: credential.token_limit || '',
        limit_period: credential.limit_period,
      }
    : { ...blankForm(), ...defaults };
  showAdvanced.value = Boolean(
    credential?.endpoint || credential?.model_override
  );
  resetCheck();
  dialogRef.value.open();
  // An LLM being edited says straight away whether its saved key still works.
  if (editingId.value) nextTick(runCheck);
};

const save = async () => {
  const payload = {
    ...form.value,
    name: form.value.name.trim() || providerName(form.value.provider),
  };
  try {
    const record = editingId.value
      ? await store.update(editingId.value, payload)
      : await store.create(payload);
    useAlert(t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.API.SUCCESS'));
    dialogRef.value.close();
    emit('saved', record);
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.API.ERROR')
    );
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="xl"
    :title="
      editingId
        ? t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.DIALOG.EDIT_TITLE')
        : t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.DIALOG.NEW_TITLE')
    "
    :description="t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.DIALOG.DESCRIPTION')"
    :confirm-button-label="
      check.state === 'failed'
        ? t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.FORM.SAVE_ANYWAY')
        : t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.FORM.SAVE')
    "
    :disable-confirm-button="!isValid"
    :is-loading="store.uiFlags.isSaving"
    @confirm="save"
  >
    <div class="flex flex-col gap-6">
      <div class="flex flex-col gap-2">
        <span class="text-heading-3 text-n-slate-12">
          {{ t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.FORM.PROVIDER.LABEL') }}
        </span>
        <div class="grid grid-cols-3 gap-2">
          <button
            v-for="(provider, key) in LLM_PROVIDERS"
            :key="key"
            type="button"
            class="flex items-center justify-center gap-2 px-3 py-3 text-sm font-medium transition-colors rounded-xl outline outline-1 -outline-offset-1 disabled:cursor-not-allowed"
            :class="
              form.provider === key
                ? 'bg-n-blue-2 outline-n-blue-8 text-n-slate-12'
                : 'bg-n-alpha-1 outline-n-weak text-n-slate-11 enabled:hover:text-n-slate-12 enabled:hover:outline-n-slate-6'
            "
            :disabled="Boolean(editingId)"
            @click="selectProvider(key)"
          >
            <Icon :icon="provider.icon" class="flex-shrink-0 size-4" />
            {{ provider.name }}
          </button>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-heading-3 text-n-slate-12">
          {{ t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.FORM.API_KEY.LABEL') }}
        </span>
        <div class="flex items-start gap-2" @paste="onKeyPasted">
          <Input
            v-model="form.api_key"
            type="password"
            autocomplete="off"
            class="flex-1"
            :placeholder="
              editingId
                ? t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.FORM.API_KEY.KEEP')
                : t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.FORM.API_KEY.PLACEHOLDER')
            "
            @blur="checkIfStale"
          />
          <Button
            type="button"
            :label="t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.CHECK.BUTTON')"
            icon="i-lucide-plug-zap"
            color="slate"
            variant="faded"
            class="flex-shrink-0"
            :is-loading="check.state === 'running'"
            :disabled="!canCheck || check.state === 'running'"
            @click="runCheck"
          />
        </div>
        <p
          class="flex items-start gap-1.5 m-0 text-xs leading-relaxed break-words"
          :class="{
            'text-n-slate-11': ['idle', 'running'].includes(check.state),
            'text-n-teal-11': check.state === 'ok',
            'text-n-ruby-11': check.state === 'failed',
          }"
        >
          <Icon
            v-if="check.state !== 'idle'"
            :icon="
              {
                running: 'i-lucide-loader-circle',
                ok: 'i-lucide-circle-check',
                failed: 'i-lucide-circle-x',
              }[check.state]
            "
            class="flex-shrink-0 size-3.5 mt-px"
            :class="{ 'animate-spin': check.state === 'running' }"
          />
          <span>{{ checkHint }}</span>
        </p>
      </div>

      <div class="flex flex-col gap-3">
        <button
          type="button"
          class="flex items-center gap-1.5 p-0 text-sm font-medium self-start text-n-slate-11 hover:text-n-slate-12"
          @click="showAdvanced = !showAdvanced"
        >
          <Icon
            icon="i-lucide-chevron-right"
            class="transition-transform size-4"
            :class="{ 'rotate-90': showAdvanced }"
          />
          {{ t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.FORM.ADVANCED') }}
        </button>
        <div
          v-if="showAdvanced"
          class="flex flex-col gap-4 ps-5 border-s border-n-weak ms-2"
        >
          <Input
            v-model="form.endpoint"
            :label="t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.FORM.ENDPOINT.LABEL')"
            :placeholder="
              t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.FORM.ENDPOINT.PLACEHOLDER')
            "
            :message="t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.FORM.ENDPOINT.HELP')"
            @blur="checkIfStale"
          />
          <Input
            v-model="form.model_override"
            :label="
              t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.FORM.MODEL_OVERRIDE.LABEL')
            "
            :placeholder="
              t(
                'CAPTAIN_SETTINGS.LLM_CREDENTIALS.FORM.MODEL_OVERRIDE.PLACEHOLDER'
              )
            "
            :message="
              t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.FORM.MODEL_OVERRIDE.HELP')
            "
          />
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-heading-3 text-n-slate-12">
          {{ t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.FORM.TOKEN_LIMIT.LABEL') }}
        </span>
        <div class="flex flex-wrap items-center gap-2">
          <Input
            v-model="form.token_limit"
            type="number"
            class="flex-1 min-w-[10rem]"
            :placeholder="
              t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.FORM.TOKEN_LIMIT.PLACEHOLDER')
            "
          />
          <span class="text-sm text-n-slate-11">
            {{ t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.FORM.TOKEN_LIMIT.PER') }}
          </span>
          <SegmentedControl
            v-model="form.limit_period"
            :options="periodOptions"
          />
        </div>
        <p class="m-0 text-xs text-n-slate-11">
          {{ t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.FORM.TOKEN_LIMIT.HELP') }}
        </p>
      </div>

      <Input
        v-model="form.name"
        :label="t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.FORM.NAME.LABEL')"
        :placeholder="providerName(form.provider)"
        :message="t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.FORM.NAME.HELP')"
      />
    </div>
  </Dialog>
</template>
